import { cssGenerator, view } from '@yola/ws-sdk';
import { createWsIframeElement } from '../../ws-iframe/helpers/create-iframe-element';
import getElementAttributesFromString from '../../ws-iframe/helpers/get-element-attributes-from-string';
import { normalizeIframeUrl } from '../../ws-iframe/validators/create-iframe-url-validator';
import formatUrl from '../../ws-iframe/helpers/format-url';
import attributes from '../../ws-iframe/constants/attributes';
import iframeEmbeddingTypes from '../../ws-iframe/constants/iframe-embedding-types';
import fallbackValues from '../constants/fallback-values';
import { iframeFieldNames } from '../constants/field-names';
import { DESKTOP_BREAKPOINT } from '../../../common/constants/breakpoints';

const { CONTENT_POSITION: CONTENT_POSITION_CLASS } = cssGenerator.constants.classNames;

const formatIframeFieldsValuesWithFallbacks = (fieldsValues) => {
  const iframeEmbeddingType = fieldsValues[iframeFieldNames.IFRAME_EMBEDDING_TYPE];

  switch (iframeEmbeddingType) {
    case iframeEmbeddingTypes.URL: {
      const urlValue = fieldsValues[iframeFieldNames.IFRAME_URL] || '';
      const normalizedUrl = normalizeIframeUrl(urlValue);
      const formattedUrl = formatUrl(normalizedUrl);

      return {
        ...fieldsValues,
        [iframeFieldNames.IFRAME_URL]: formattedUrl,
      };
    }

    case iframeEmbeddingTypes.CODE: {
      return {
        ...fieldsValues,
        [iframeFieldNames.IFRAME_CODE]: fieldsValues[iframeFieldNames.IFRAME_CODE] || '',
      };
    }

    default: {
      return {
        ...fieldsValues,
        [iframeFieldNames.IFRAME_URL]: fallbackValues.IFRAME_SRC,
      };
    }
  }
};

const mapIframeFieldsValuesToAttributes = (fieldsValues) => {
  const iframeEmbeddingType = fieldsValues[iframeFieldNames.IFRAME_EMBEDDING_TYPE];

  switch (iframeEmbeddingType) {
    case iframeEmbeddingTypes.URL: {
      return {
        src: fieldsValues[iframeFieldNames.IFRAME_URL],
        ...(fieldsValues[iframeFieldNames.IFRAME_ALLOW_FULLSCREEN] && {
          [attributes.ALLOW_FULLSCREEN]: true,
        }),
      };
    }

    case iframeEmbeddingTypes.CODE: {
      return getElementAttributesFromString(fieldsValues[iframeFieldNames.IFRAME_CODE]);
    }

    default: {
      return {
        src: fieldsValues[iframeFieldNames.IFRAME_URL] || fallbackValues.IFRAME_SRC,
      };
    }
  }
};

const buildIframeElementFromFieldsValues = (fieldsValues) => {
  const formattedFieldsValues = formatIframeFieldsValuesWithFallbacks(fieldsValues);
  const iframeAttributes = mapIframeFieldsValuesToAttributes(formattedFieldsValues);

  return createWsIframeElement(iframeAttributes);
};

const updateIframeMediaElementFromFieldsValues = (mediaContainerElement) => {
  if (mediaContainerElement.hasAttribute('placeholder')) {
    // Iframes have a working fallback src,
    // so the placeholder `placeholder` attribute is not needed and should be removed if present to show correct content.
    mediaContainerElement.removeAttribute('placeholder');
  }

  // If the previous content was an image, users may have adjusted the content scale and position.
  // We need to reset these properties to their default values for iframes, so that the iframe is displayed correctly.
  const currentClass = mediaContainerElement.getAttribute('class') || '';
  const newClass = view.helpers.getPatchedResponsiveClass({
    currentClass,
    className: CONTENT_POSITION_CLASS,
    value: fallbackValues.IMAGE_POSITION,
    breakpoint: DESKTOP_BREAKPOINT,
  });
  mediaContainerElement.setAttribute('class', newClass);
  mediaContainerElement.setAttribute('content-scale', fallbackValues.IMAGE_SCALE);
};

export { updateIframeMediaElementFromFieldsValues };
export default buildIframeElementFromFieldsValues;
